// @flow

import * as React from 'react'
import { connect, type Connector } from 'react-redux'

import XLSXUploader from './xlsx-uploader'
import authenticatedRequest from '../store/authenticated-request'

import type { ReduxState, Dispatch } from '../store'
import type { ActiveItem } from '../app'

type OwnProps = {|
  activeItem: ActiveItem,
|}

type MappedProps = {|
  updateStatus: boolean,
  campaignName: string,
  panelRecordColumns: Array<string>,
  strictColumnsCheck: boolean,
|}

const mapStateToProps = (state: ReduxState, ownProps: OwnProps): MappedProps => {
  let defaultPanelColumns = [
    'id',
    'nome',
    'cognome',
    'area',
    'indirizzo',
    'citta',
    'provincia',
    'regione',
    'cap',
    'specializzazione',
    'stato_f2f',
    'stato_phone',
    'stato_web',
  ]
  let panelRecordColumns = []
  let strictColumnsCheck = true

  let grid = state.panel.grid
  if (!grid.columns) grid = { ...grid, columns: [] }
  if (grid.columns.length === 0) {
    strictColumnsCheck = false
    panelRecordColumns = defaultPanelColumns
  } else {
    strictColumnsCheck = true
    panelRecordColumns = grid.columns.map(el => {
      return el.key
    })
    panelRecordColumns[0] = 'id'
  }

  return {
    updateStatus: state.app.updateStatus,
    campaignName: state.allowedCampaigns.currentCampaign,
    panelRecordColumns,
    strictColumnsCheck,
  }
}

type Props = { dispatch: Dispatch } & OwnProps & MappedProps

class SystemSidebar extends React.Component<Props> {
  defaultVisitColumns = ['id_medico', 'data_visita', 'prodotto', 'canale', 'isf', 'call_experience', 'wave']

  validateDoctors = async data => {
    let response = {}
    try {
      response = await authenticatedRequest.get('/api/panels/' + this.props.campaignName + '/allIdMedico')
    } catch (err) {
      return { data: [], valid: false, message: err.response.data.message }
    }
    let doctorIDs = response.data.data
    let message = ''
    // Skip duplicate IDs
    let validData = data.filter(doctor => {
      if (doctorIDs.indexOf(doctor[0]) !== -1) {
        return false
      } else {
        return doctor
      }
    })

    let skippedCount = data.length - validData.length
    message = skippedCount ? 'Skipped records (gia presenti): ' + skippedCount : ''
    return { data: validData, valid: true, message }
  }

  validateDoctorsStatus = async data => {
    let response = {}
    try {
      response = await authenticatedRequest.get('/api/panels/' + this.props.campaignName + '/allIdMedico')
    } catch (err) {
      return { data: [], valid: false, message: err.response.data.message }
    }
    let doctorIDs = response.data.data

    let message = ''
    let valid = false
    let invalidIDs = {}
    let validatedData = data.filter(status => {
      status[0] = status[0].trim()
      status[1] = status[1].trim()
      // MISSING ID
      if (doctorIDs.indexOf(status[0]) === -1) {
        invalidIDs[status[0]] = 1
        return false
      }
      // INVALID STATUS
      if (
        status[0] !== '' &&
        /^(\s*|y|yes|n|no|si|s|abilitato|attivo|non abilitato|non attivo|disabilitato)$/i.test(status[1])
      ) {
        return status
      } else {
        return false
      }
    })

    if (Object.keys(invalidIDs).length > 0) {
      message = 'Rilevati id_medico inesistenti: ' + Object.keys(invalidIDs).join(', ')
    } else if (data.length !== validatedData.length) {
      message = 'Rilevati record con campi obbligatori non correttamente valorizzati'
    } else {
      valid = true
    }

    return { data: valid ? validatedData : [], valid, message }
  }

  validateVisits = async data => {
    let response = {}
    try {
      response = await authenticatedRequest.get('/api/visits/' + this.props.campaignName + '/dates')
    } catch (err) {
      return { data: [], valid: false, message: err.response.data.message }
    }
    let presentVisits = response.data.data

    let message = ''
    let valid = false
    let validRecords = 0
    let countIDs = {}
    let invalidIDs = {}

    let validatedData = data.filter(visit => {
      visit[3] = visit[3].toUpperCase()
      // DUPLICATES
      let k = visit[0] + '-' + visit[1]
      countIDs = { ...countIDs, [k]: (countIDs[k] || 0) + 1 }
      // INVALID ID
      if (Object.keys(presentVisits).indexOf(visit[0]) === -1) {
        invalidIDs[visit[0]] = 1
        return false
      }
      // VOID MANDATORY FIELDS
      if (visit[0] !== '' && /^20[0-9]{6}$/.test(visit[1]) && /WEB|F2F|PHONE/.test(visit[3]) && visit[2] !== '') {
        validRecords++
        if (presentVisits[visit[0]].indexOf(parseInt(visit[1], 10)) === -1) {
          return visit
        } else {
          return false
        }
      }
      return false
    })

    let dups = Object.keys(countIDs).filter(c => countIDs[c] > 1)
    if (dups.length > 0) {
      message = 'Rilevati record duplicati: ' + dups.join(' - ').replace('-', ',')
    } else if (Object.keys(invalidIDs).length > 0) {
      message = 'Rilevati id_medico inesistenti: ' + Object.keys(invalidIDs).join(', ')
    } else if (data.length !== validRecords) {
      message = 'Rilevati record con campi obbligatori non correttamente valorizzati'
    } else {
      valid = true
      message = 'Skipped records (gia presenti): ' + (data.length - validatedData.length)
    }

    return { data: valid ? validatedData : [], valid, message }
  }

  render() {
    let renderPanelUploader
    if (this.props.updateStatus) {
      renderPanelUploader = (
        <XLSXUploader
          columns={[]}
          updateStatus={true}
          strictColumnsCheck={this.props.strictColumnsCheck}
          campaignName={this.props.campaignName}
          chunkSize={200}
          checkRecords={true}
          checkRecordsCallback={this.validateDoctorsStatus}
          title="Aggiornamento status panelisti"
          uploadUrl={'/api/panels/' + this.props.campaignName + '/doctors/updateStatus'}
        />
      )
    } else {
      renderPanelUploader = (
        <XLSXUploader
          columns={this.props.panelRecordColumns}
          strictColumnsCheck={this.props.strictColumnsCheck}
          campaignName={this.props.campaignName}
          chunkSize={200}
          title="Caricamento dati panel"
          checkRecords={true}
          checkRecordsCallback={this.validateDoctors}
          uploadUrl={'/api/panels/' + this.props.campaignName + '/doctors/upload'}
        />
      )
    }

    return (
      <div>
        {this.props.activeItem === 'panels' && renderPanelUploader}
        {this.props.activeItem === 'visits' && (
          <XLSXUploader
            columns={this.defaultVisitColumns}
            strictColumnsCheck={true}
            campaignName={this.props.campaignName}
            chunkSize={200}
            title="Caricamento dati visite"
            checkRecords={true}
            checkRecordsCallback={this.validateVisits}
            uploadUrl={'/api/visits/' + this.props.campaignName + '/upload'}
          />
        )}
      </div>
    )
  }
}

const connector: Connector<OwnProps, MappedProps> = connect(mapStateToProps, null)
export default connector(SystemSidebar)



// WEBPACK FOOTER //
// ./src/systemwide/system-sidebar.js